import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from './supabaseClient'; // Make sure this file exists
import AdminPanel from './AdminPanel'; // Make sure this file exists
import logo from './logo.jpeg'; 

// ==========================================
// ALL COMPONENTS IMPORT
// ==========================================
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import AboutSection from './components/AboutSection';
import ServicesSection from './components/ServicesSection';
import BlogSection from './components/BlogSection';
import VideoSection from './components/VideoSection';
import FAQSection from './components/FAQSection';
import BookingSection from './components/BookingSection';
import FloatingWhatsApp from './components/FloatingWhatsApp';
import BlogModal from './components/BlogModal';
import AdminLoginModal from './components/AdminLoginModal';
import TestimonialSection from './components/TestimonialSection';
import MindfulnessZone from './components/MindfulnessZone';

// ==========================================
// CUSTOM HOOK: Data Fetching
// ==========================================
function useSupabaseData(tableName, orderByCol = 'created_at') {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const { data: result, error } = await supabase
      .from(tableName)
      .select('*')
      .order(orderByCol, { ascending: false });
      
    if (result) setData(result);
    if (error) console.error(`${tableName} fetch error:`, error);
    setLoading(false);
  }, [tableName, orderByCol]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading };
}

// ==========================================
// MAIN APP COMPONENT
// ==========================================
function App() {
  // Fetching Data
  const { data: videos, loading: videosLoading } = useSupabaseData('videos');
  const { data: blogs, loading: blogsLoading } = useSupabaseData('blogs');

  // UI States
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [visibleVideos, setVisibleVideos] = useState(6);
  const [visibleBlogs, setVisibleBlogs] = useState(3);
  
  // Auth States
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);

  // Check if Admin is already logged in (Persist Session)
  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setIsAdminLoggedIn(true);
      }
    };
    checkUser();
  }, []);

  // Handlers
  const loadMoreBlogs = () => setVisibleBlogs(prev => prev + 3);
  const loadMoreVideos = () => setVisibleVideos(prev => prev + 6);

  // Route to Admin Panel if logged in
  if (isAdminLoggedIn) return <AdminPanel />;

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-deep-charcoal font-sans selection:bg-ocean-teal selection:text-white scroll-smooth relative">
      
      {/* 1. Header & Hero */}
      <Navbar />
      <HeroSection />
      
      {/* 2. Professional Profile */}
      <AboutSection />
      <ServicesSection />
      <TestimonialSection />
      <MindfulnessZone />
      {/* 3. Content Hub */}
      <BlogSection 
        blogs={blogs} 
        visibleBlogs={visibleBlogs} 
        loading={blogsLoading} 
        loadMoreBlogs={loadMoreBlogs} 
        setSelectedBlog={setSelectedBlog} 
      />

      <VideoSection 
        videos={videos} 
        visibleVideos={visibleVideos} 
        loading={videosLoading} 
        loadMoreVideos={loadMoreVideos} 
        setPlayingVideo={setPlayingVideo} 
      />

      {/* 4. Help & Contact */}
      <FAQSection />
      <BookingSection />
      
      {/* 5. Client Magnet Tool */}
      <FloatingWhatsApp />

      {/* ================= MODALS & OVERLAYS ================= */}
      
      {/* Blog Detail Modal */}
      <BlogModal 
        blog={selectedBlog} 
        onClose={() => setSelectedBlog(null)} 
      />

      {/* Video Player Overlay */}
      {playingVideo && (
        <div 
          className="fixed inset-0 z-[100] bg-deep-charcoal/95 backdrop-blur-xl flex items-center justify-center p-4 md:p-10"
          role="dialog"
          aria-modal="true"
        >
          <button 
            onClick={() => setPlayingVideo(null)} 
            className="absolute top-6 right-6 md:top-10 md:right-10 text-white/50 hover:text-white text-2xl w-12 h-12 flex items-center justify-center rounded-full hover:bg-white/10 transition-all z-50"
            aria-label="Close video player"
          >
            ✕
          </button>
          <div className="w-full max-w-5xl aspect-video bg-black rounded-2xl md:rounded-[2rem] overflow-hidden shadow-2xl ring-4 ring-white/10 animate-scale-up relative">
            <iframe 
              src={playingVideo.replace('watch?v=', 'embed/').replace('shorts/', 'embed/')} 
              className="w-full h-full absolute inset-0" 
              title="YouTube Video Player"
              allowFullScreen
              autoPlay
            ></iframe>
          </div>
        </div>
      )}

      {/* Secure Admin Login Modal */}
      {showLoginModal && (
        <AdminLoginModal 
          onClose={() => setShowLoginModal(false)}
          onLoginSuccess={() => {
            setIsAdminLoggedIn(true);
            setShowLoginModal(false);
          }}
        />
      )}

      {/* ================= FOOTER ================= */}
      <footer className="py-12 md:py-16 px-6 text-center bg-white border-t border-gray-50">
        <div className="max-w-6xl mx-auto flex flex-col items-center space-y-6">
          <img src={logo} alt="Parinita Dutta Roy Logo" className="w-12 h-12 rounded-full opacity-50 grayscale hover:grayscale-0 hover:opacity-100 transition-all cursor-pointer" />
          <div className="text-xs font-black uppercase tracking-[0.4em] text-deep-charcoal/40">
            © {new Date().getFullYear()} Parinita Dutta Roy. All Rights Reserved.
          </div>
          <button 
            onClick={() => setShowLoginModal(true)}
            className="text-[9px] font-black uppercase tracking-[0.5em] text-gray-300 hover:text-ocean-teal transition-colors mt-8 focus:outline-none"
          >
            Admin Access
          </button>
        </div>
      </footer>
      
    </div>
  );
}

export default App;